import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagsService, type CreateTagInput, type UpdateTagInput } from '../services/tags';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';

/**
 * Hook to fetch all tags
 */
export function useTags() {
  return useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsService.getAll(),
  });
}

/**
 * Hook to fetch a single tag by ID
 */
export function useTag(id: string) {
  return useQuery({
    queryKey: ['tags', id],
    queryFn: () => tagsService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTagInput) => tagsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag créé avec succès');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ||'Erreur lors de la création du tag');
    },
  });
}

/**
 * Hook to update a tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTagInput }) =>
      tagsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag mis à jour avec succès');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ||'Erreur lors de la mise à jour du tag');
    },
  });
}

/**
 * Hook to delete a tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tagsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag supprimé avec succès');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ||'Erreur lors de la suppression du tag');
    },
  });
}

/**
 * Hook to reorder tags
 */
export function useReorderTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tags: { id: string; order: number }[]) =>
      tagsService.reorder(tags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tags réordonnés avec succès');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ||'Erreur lors du réordonnement des tags');
    },
  });
}
